import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type { WorkScheduleFindInterface } from '@zaoblako/shared/modules/security/dtos/work-schedule/work-schedule-find.dto';
import {
  WorkScheduleFormDto,
  type WorkScheduleFormInterface
} from '@zaoblako/shared/modules/security/dtos/work-schedule/work-schedule-form.dto';
import type { WorkScheduleHoursInterface } from '@zaoblako/shared/modules/security/dtos/work-schedule/work-schedule-hours.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { WorkScheduleService } from '@zaoblako/web/core/services/security/work-schedule.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { TranslateWeekDaysPipe } from '@zaoblako/web/shared/pipes/translate-week-days.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-schedule-form',
  templateUrl: './schedule-form.component.html',
  styleUrl: './schedule-form.component.scss',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    InputNumberModule,
    FormsModule,
    TableComponent,
    JoinPipe,
    ButtonComponent,
    TranslateWeekDaysPipe,
    SpinnerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScheduleFormComponent extends BaseFormComponent<WorkScheduleFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public schedule: WorkScheduleFormInterface = {
    name: '',
    hours: {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0
    }
  };

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public dto = WorkScheduleFormDto;

  public workDaysFields: [string, number][] = [];

  public constructor(
    private readonly workScheduleService: WorkScheduleService,
    private readonly formStoreService: FormStoreService,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (!this.schedule.hours) {
      this.schedule.hours = {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0
      };
    }

    this.workDaysFields = Object.entries(this.schedule.hours).map(([key, value]) => {
      return [key, value ?? 0];
    });

    this.setWorkHours();

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.workScheduleService
      .findById<WorkScheduleFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (schedule) => {
          this.schedule = schedule;
          this.workDaysFields = Object.entries(this.schedule.hours).map(([key, value]) => {
            return [key, value ?? 0];
          });
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveSchedule() {
    if (this.checkValidate(this.schedule).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.workScheduleService.update<WorkScheduleFormInterface, WorkScheduleFindInterface>(this.schedule).subscribe({
          next: (schedule) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`График работы "${schedule.name}" обновлен`);
            this.formStoreService.saveNext(schedule, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.workScheduleService.create<WorkScheduleFindInterface>(this.schedule).subscribe({
          next: (schedule) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('График работы создан');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/user/work-schedule/card/' + schedule.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(schedule, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteSchedule() {
    this.confirmService.confirm({
      message: `Вы действительно желаете удалить график работы "${this.schedule?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.workScheduleService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(`График работы "${this.schedule?.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }

  public setWorkHours() {
    this.schedule.hours = { ...Object.fromEntries(this.workDaysFields) } as any as WorkScheduleHoursInterface;
  }
}
